/**
 * Helpers to recover from stale client assets after a production deploy
 * (service worker / Cache Storage / missing webpack chunks).
 */

const VERSION_RELOAD_KEY = 'mlsms-version-reload';
const CHUNK_RELOAD_KEY = 'mlsms-chunk-reload';
const SW_RELOAD_KEY = 'mlsms-sw-reload';
const RELOAD_COOLDOWN_MS = 30000;

function getSessionItem(key) {
    if(typeof sessionStorage === 'undefined') {
        return null;
    }
    try {
        return sessionStorage.getItem(key);
    } catch(err) {
        return null;
    }
}

function setSessionItem(key, value) {
    if(typeof sessionStorage === 'undefined') {
        return;
    }
    try {
        sessionStorage.setItem(key, value);
    } catch(err) {
        // Ignore quota / private-mode failures
    }
}

function removeSessionItem(key) {
    if(typeof sessionStorage === 'undefined') {
        return;
    }
    try {
        sessionStorage.removeItem(key);
    } catch(err) {
        // Ignore
    }
}

function isWithinCooldown(key) {
    const raw = getSessionItem(key);
    if(!raw) {
        return false;
    }
    const at = Number(raw);
    if(!Number.isFinite(at)) {
        return true;
    }
    return Date.now() - at < RELOAD_COOLDOWN_MS;
}

function markReloadAttempt(key) {
    setSessionItem(key, String(Date.now()));
}

/**
 * Unregister all service workers and clear Cache Storage so the next
 * navigation fetches the current HTML + hashed assets from the network.
 */
export function clearServiceWorkersAndCaches() {
    const tasks = [];

    if(typeof navigator !== 'undefined' && navigator.serviceWorker) {
        tasks.push(
            navigator.serviceWorker.getRegistrations()
                .then(registrations => Promise.all(registrations.map(reg => reg.unregister())))
                .catch(() => null)
        );
    }

    if(typeof caches !== 'undefined') {
        tasks.push(
            caches.keys()
                .then(keys => Promise.all(keys.map(key => caches.delete(key))))
                .catch(() => null)
        );
    }

    return Promise.all(tasks);
}

/**
 * Hard reload after a stale deploy. Guarded to avoid infinite loops.
 * - version / chunk: unregister SW + clear Cache Storage, then reload
 * - sw: reload only (new SW already applied via OfflinePlugin.applyUpdate)
 * @param {'version'|'chunk'|'sw'} reason
 * @param {string} [versionToken] server version when reason === 'version'
 * @returns {Promise<boolean>} true if a reload was triggered
 */
export function hardReloadForStaleApp(reason, versionToken) {
    if(typeof window === 'undefined') {
        return Promise.resolve(false);
    }

    if(reason === 'version') {
        const previous = getSessionItem(VERSION_RELOAD_KEY);
        // Already reloaded for this exact server version and still stale → stop looping
        if(previous === versionToken) {
            return Promise.resolve(false);
        }
        setSessionItem(VERSION_RELOAD_KEY, versionToken || '1');
    } else if(reason === 'chunk') {
        if(getSessionItem(CHUNK_RELOAD_KEY)) {
            return Promise.resolve(false);
        }
        markReloadAttempt(CHUNK_RELOAD_KEY);
    } else if(reason === 'sw') {
        if(isWithinCooldown(SW_RELOAD_KEY)) {
            return Promise.resolve(false);
        }
        markReloadAttempt(SW_RELOAD_KEY);
        window.location.reload();
        return Promise.resolve(true);
    }

    return clearServiceWorkersAndCaches().finally(() => {
        window.location.reload();
    }).then(() => true);
}

export function clearVersionReloadGuardIfMatched(clientVersion, serverVersion) {
    if(clientVersion && serverVersion && clientVersion === serverVersion) {
        removeSessionItem(VERSION_RELOAD_KEY);
        removeSessionItem(CHUNK_RELOAD_KEY);
    }
}

export function isChunkLoadError(error) {
    if(!error) {
        return false;
    }
    const name = error.name || '';
    const message = String(error.message || error || '');
    return (
        name === 'ChunkLoadError'
        || /Loading chunk [\d]+ failed/i.test(message)
        || /Failed to fetch dynamically imported module/i.test(message)
        || /Importing a module script failed/i.test(message)
    );
}

/**
 * Install global listeners that hard-reload once on webpack chunk failures.
 */
export function installChunkLoadRecovery() {
    if(typeof window === 'undefined') {
        return;
    }

    const tryRecover = error => {
        if(!isChunkLoadError(error)) {
            return;
        }
        hardReloadForStaleApp('chunk');
    };

    window.addEventListener('error', event => {
        tryRecover(event.error || event.message);
    });

    window.addEventListener('unhandledrejection', event => {
        tryRecover(event.reason);
    });
}
